/**
 * The terminal gate (R11) — how `processPlayerInput` answers a player whose
 * story has already ended.
 *
 * @remarks
 * A TERMINAL ending closes the world: the input is echoed (the player's
 * words always belong to the transcript), the parser never runs, no turn is
 * consumed, and the answer is the ending's own `refusalNarratives` — the
 * specific voice wins, the D47 law — or the `storyHasEnded` slot when the
 * ending authored none. An OPEN ending never reaches this module: the world
 * stays playable and every input walks the ordinary pipeline.
 *
 * Extracted from `process-player-input.ts` at the 500-line cap; it is one
 * self-contained reading with one caller.
 *
 * @packageDocumentation
 */

import type { RuntimeState, RuntimeTranscriptEntry } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { PlayerInputOutcome } from './process-player-input.js';

import { bumpResponseUsage, speakSlot } from '../defaults/default-responses.js';
import { terminalEndingOf } from '../narrative/session-ended.js';
import { appendTranscript, inputNamespaceCursor, stampNarrativeLines } from './transcript-cursor.js';

/**
 * Refuse this input when a terminal ending has been selected.
 *
 * @param bundle - The loaded bundle
 * @param openedState - The state WITH history + echo already committed
 * @param inputOrdinal - This input's ordinal (id namespace)
 * @param echoEntries - The player-echo entries (head of this input's entry list)
 * @returns The committed `session_ended` outcome, or `undefined` when the world is still open
 */
export const refuseBecauseStoryEnded = (
	bundle: EngineGameBundle,
	openedState: RuntimeState,
	inputOrdinal: number,
	echoEntries: readonly RuntimeTranscriptEntry[],
): PlayerInputOutcome | undefined => {
	const terminalEnding = terminalEndingOf(bundle, openedState);

	if (terminalEnding === undefined) {
		return undefined;
	}

	const refusalCursor = inputNamespaceCursor(inputOrdinal, echoEntries.length, openedState);

	// The ending's own lines outrank the generic slot (the D47 law: the
	// specific voice wins wherever both could speak).
	if (terminalEnding.refusalNarratives !== undefined) {
		const stamped = stampNarrativeLines(bundle, openedState, refusalCursor, terminalEnding.refusalNarratives, `ending:${terminalEnding.endingId}`);

		return {
			corrections: [],
			nextState: appendTranscript(openedState, stamped.entries),
			transcriptEntries: [...echoEntries, ...stamped.entries],
			type: 'session_ended',
		};
	}

	const spoken = speakSlot(bundle, openedState, 'storyHasEnded');
	const stamped = stampNarrativeLines(bundle, openedState, refusalCursor, [spoken.line], 'session_ended');
	const refusedState = appendTranscript(openedState, stamped.entries);

	return {
		corrections: [],
		nextState: {
			...refusedState,
			session: {
				...refusedState.session,
				responseUsage: bumpResponseUsage(refusedState.session.responseUsage, [spoken.slot]),
			},
		},
		transcriptEntries: [...echoEntries, ...stamped.entries],
		type: 'session_ended',
	};
};
